/**
 * Safe JSON parser utilities to prevent XSS attacks from localStorage
 * These functions validate and sanitize JSON data before parsing
 */

/**
 * Safe JSON parse with validation
 * @param jsonString - String to parse
 * @param fallback - Fallback value if parsing fails or data is invalid
 * @returns Parsed and validated object or fallback
 */
export function safeJSONParse<T = unknown>(jsonString: string | null, fallback: T): T {
  if (!jsonString || typeof jsonString !== 'string') {
    return fallback;
  }

  // Basic XSS protection - reject strings containing script tags or javascript:
  if (
    jsonString.includes('<script>') ||
    jsonString.includes('</script>') ||
    jsonString.includes('javascript:') ||
    jsonString.includes('eval(') ||
    jsonString.includes('Function(')
  ) {
    console.warn('[SafeParse] XSS attempt detected in localStorage data');
    return fallback;
  }

  try {
    const parsed = JSON.parse(jsonString);
    return parsed as T;
  } catch (error) {
    console.warn('[SafeParse] Invalid JSON in localStorage:', error);
    return fallback;
  }
}

/**
 * Safe offline user parser with strict validation
 * @param cachedUserStr - Cached user string from localStorage
 * @returns Validated user object or null
 */
export function safeParseOfflineUser(
  cachedUserStr: string | null
): { id: string; email?: string } | null {
  const parsed = safeJSONParse(cachedUserStr, null);

  if (!parsed || typeof parsed !== 'object') {
    return null;
  }

  const user = parsed as any;

  // Validate required fields
  if (!user.id || typeof user.id !== 'string') {
    console.warn('[SafeParse] Invalid offline user data - missing or invalid id');
    return null;
  }

  // Basic UUID/ID format validation (alphanumeric + hyphens)
  if (!/^[a-zA-Z0-9-]+$/.test(user.id)) {
    console.warn('[SafeParse] Invalid offline user data - suspicious id format');
    return null;
  }

  // Return only safe fields
  return {
    id: user.id,
    ...(user.email &&
      typeof user.email === 'string' &&
      user.email.includes('@') && { email: user.email }),
  };
}

/**
 * Safe array parser with type validation
 * @param jsonString - Array string from localStorage
 * @param fallback - Fallback array if parsing fails
 * @returns Parsed and validated array or fallback
 */
export function safeParseArray<T>(jsonString: string | null, fallback: T[]): T[] {
  const parsed = safeJSONParse(jsonString, fallback);

  if (!Array.isArray(parsed)) {
    console.warn('[SafeParse] Expected array but got:', typeof parsed);
    return fallback;
  }

  return parsed;
}
